// @flow

// MQTT(AWS IoT) 구독·발행은 전부 이 모듈을 거친다 (#145).
//
// @aws-amplify/pubsub 4.x는 연결이 끊기면 구독 observer에 error만 던지고 재연결·재구독을
// 호출자에게 맡긴다. 그런데 우리 구독들은 로그만 찍어서, 한 번 끊기면 새로고침 전까지
// 파트너 목록·파트너 자막·시트 공유가 모두 멈췄다.
//
// 더해서, 재연결을 시도하는 순간 아직 오프라인이면 실패한 연결 promise가 라이브러리의
// clientsQueue에 그대로 캐시돼 이후 모든 구독·발행이 즉시 실패한다. 그래서 재시도 전에
// "죽은" 클라이언트(실패했거나 연결이 끊긴 것)만 캐시에서 걷어낸다. 살아 있는 클라이언트는
// 건드리지 않는다.

import { PubSub } from "aws-amplify";
import { AWSIoTProvider } from "@aws-amplify/pubsub";
import * as Sentry from "@sentry/react";

export const iotProvider: any = new AWSIoTProvider({
  aws_pubsub_region: "ap-northeast-2",
  aws_pubsub_endpoint:
    "wss://a3uo2wikx5ezjw-ats.iot.ap-northeast-2.amazonaws.com/mqtt",
});

// 재구독 간격: 처음엔 빠르게, 오래 끊기면 15초마다
export const RESUBSCRIBE_DELAYS_MS = [1000, 2000, 5000, 10000, 15000];
const REPORT_THROTTLE_MS = 60 * 1000;

let pubsub: any = PubSub;
let provider: any = iotProvider;

// 테스트 전용: 실제 PubSubClass·MqttOverWSProvider에 가짜 전송 계층을 붙여 검증한다
export function setMqttDepsForTest(deps: { pubsub: any, provider: any }) {
  pubsub = deps.pubsub;
  provider = deps.provider;
}

// 연결 상태 표시등(#204)용: 지금 재연결을 기다리는 구독 수
const retrying: Set<{}> = new Set();
const retryListeners: Set<() => void> = new Set();
function setRetrying(token: {}, on: boolean) {
  if (retrying.has(token) === on) {
    return;
  }
  if (on) {
    retrying.add(token);
  } else {
    retrying.delete(token);
  }
  retryListeners.forEach((fn) => fn());
}
export function getMqttRetryingCount(): number {
  return retrying.size;
}
export function onMqttRetryingChange(fn: () => void): () => void {
  retryListeners.add(fn);
  return () => {
    retryListeners.delete(fn);
  };
}

const lastReported: Map<string, number> = new Map();
function report(context: string, message: string, extra: Object) {
  // 오프라인이면 어차피 전송되지 않는다
  if (navigator.onLine === false) {
    return;
  }
  const now = Date.now();
  if (now - (lastReported.get(context) ?? 0) < REPORT_THROTTLE_MS) {
    return;
  }
  lastReported.set(context, now);
  Sentry.captureException(new Error(`${context}: ${message}`), { extra });
}

function errorMessage(err: any): string {
  const inner = err?.error ?? err;
  return String(inner?.message ?? inner ?? "unknown error");
}

// 캐시된 클라이언트가 실패했거나 연결이 끊겼으면 걷어내 다음 연결 때 새로 만들게 한다
export async function resetDeadMqttClient(): Promise<void> {
  const queue = provider?.clientsQueue;
  const clientId = provider?.clientId;
  const cached = queue?.promises?.get(clientId);
  if (!cached) {
    return;
  }
  let client = null;
  try {
    client = await cached;
  } catch (e) {
    client = null;
  }
  // 기다리는 사이 다른 쪽이 이미 새 클라이언트를 만들었으면 그대로 둔다
  if (queue.promises.get(clientId) !== cached) {
    return;
  }
  if (client == null || !client.isConnected()) {
    queue.remove(clientId);
  }
}

// 끊기면 알아서 다시 구독하는 MQTT 구독. 반환값은 기존 구독처럼 unsubscribe()를 가진다.
// onResubscribed: 재구독할 때마다 불린다 (예: presence 목록을 즉시 다시 요청)
export function subscribeMqtt(
  topic: string,
  handlers: {
    next: (data: any) => mixed,
    onResubscribed?: () => mixed,
    context: string,
  }
): { unsubscribe: () => void } {
  const token = {};
  let stopped = false;
  let attempt = 0;
  let current = null;
  let timer = null;

  const start = () => {
    if (stopped) {
      return;
    }
    current = pubsub.subscribe(topic).subscribe({
      next: (data) => {
        attempt = 0;
        handlers.next(data);
      },
      error: (err) => {
        if (stopped) {
          return;
        }
        setRetrying(token, true);
        const delay =
          RESUBSCRIBE_DELAYS_MS[Math.min(attempt, RESUBSCRIBE_DELAYS_MS.length - 1)];
        attempt += 1;
        console.warn(`[${handlers.context}] MQTT subscription lost, retry in ${delay}ms`, err);
        report(handlers.context, `MQTT subscription lost: ${errorMessage(err)}`, {
          topic,
          attempt,
          rawError: err,
        });
        timer = setTimeout(resubscribe, delay);
      },
    });
  };

  const resubscribe = async () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (stopped) {
      return;
    }
    // 오프라인이면 붙을 수 없으니 online 이벤트를 기다린다 (혹시 놓쳐도 15초 뒤 다시 본다).
    // 오프라인에서 재연결을 시도하면 실패한 WebSocket 연결만 콘솔에 쌓인다
    if (navigator.onLine === false) {
      timer = setTimeout(
        resubscribe,
        RESUBSCRIBE_DELAYS_MS[RESUBSCRIBE_DELAYS_MS.length - 1]
      );
      return;
    }
    await resetDeadMqttClient();
    if (stopped) {
      return;
    }
    setRetrying(token, false);
    start();
    if (handlers.onResubscribed) {
      handlers.onResubscribed();
    }
  };

  // 네트워크가 돌아왔다고 브라우저가 알려주면 남은 대기 시간을 건너뛴다
  const onOnline = () => {
    if (timer) {
      resubscribe();
    }
  };
  window.addEventListener("online", onOnline);

  start();

  return {
    unsubscribe: () => {
      stopped = true;
      setRetrying(token, false);
      window.removeEventListener("online", onOnline);
      if (timer) {
        clearTimeout(timer);
      }
      try {
        current?.unsubscribe();
      } catch (e) {
        // 끊긴 클라이언트의 정리 경로에서 라이브러리가 던질 수 있다 — 이미 끊긴 구독이라 무시
      }
    },
  };
}

// 실패하면 죽은 클라이언트를 걷어내고 한 번 더 보낸다
export async function publishMqtt(
  topic: string,
  message: mixed,
  context: string
): Promise<boolean> {
  // 오프라인이면 보낼 수 없다 — 시도하면 실패한 연결·경고만 쌓인다
  if (navigator.onLine === false) {
    return false;
  }
  try {
    await pubsub.publish(topic, message);
    return true;
  } catch (first) {
    await resetDeadMqttClient();
    try {
      await pubsub.publish(topic, message);
      return true;
    } catch (second) {
      console.warn(`[${context}] MQTT publish failed`, second);
      report(context, `MQTT publish failed: ${errorMessage(second)}`, { topic, rawError: second });
      return false;
    }
  }
}
